import { toast } from "sonner";
import { parseError } from "@/api/errors";

/**
 * Shows a failure that has nowhere else to go -- a toggle, a drag, a menu
 * action, a background save. A failure inside a form or dialog is shown in
 * that form (`FormAlert`), and one beside a field under it (`FieldError`);
 * see "Errors" in docs/UI-ARCHITECTURE.md §13.
 *
 * `context` says what was being attempted ("Couldn't pin the collection"); the
 * server's own message becomes the description under it. Toasts are keyed by
 * error code, so a burst of the same failure -- every drag of a reorder while
 * offline -- replaces itself instead of stacking.
 *
 * The only place the app calls `toast.error`.
 */
export function notifyError(e: unknown, context?: string) {
  const error = parseError(e);
  if (error.status === "aborted") return;
  toast.error(context ?? error.message, {
    id: context ? `${context}|${error.code}` : error.code,
    description: context ? error.message : undefined,
  });
}

/** `promise.catch(notifyErrorWith("Couldn't …"))` for fire-and-forget calls. */
export function notifyErrorWith(context: string) {
  return (e: unknown) => notifyError(e, context);
}

/** One toast for "your session ended", however many requests discover it. */
export function notifySignedOut() {
  toast.error("Your session has expired", {
    id: "auth.signed_out",
    description: "Sign in again to keep working.",
  });
}
